/**
 * Apple TV → Media. A direct port of the corporate dashboard's
 * appletv-media-library: the same thumbnail grid with hover preview/delete, the
 * same drop-zone upload with progress. Only the styling is Prospera's.
 *
 * Uploads go straight from the browser to storage on a short-lived signed URL,
 * so a large video never passes through our API.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Film, ImageOff, Search, Trash2, Upload } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import {
  deleteMedia,
  getMediaUploadUrl,
  listMedia,
  registerMedia,
  uploadToStorage,
  type AppleTvMedia,
} from '@/services/appleTv.service';
import { isNoLongerAvailable, SHARED_PLAYLIST_BADGE, signageErrorMessage } from './sharedPlaylists';
import { Badge } from '@/components/ui/Badge';
import { AppleTvGate, AppleTvPageShell, shouldGate, useAppleTvAccess } from './useAppleTvAccess';
import { Table } from './AppleTvTable';

const ALLOWED_IMAGE = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];
const ALLOWED_VIDEO = ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm', 'video/mpeg'];
const ACCEPT = [...ALLOWED_IMAGE, ...ALLOWED_VIDEO].join(',');

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AppleTvMediaPage() {
  const access = useAppleTvAccess();
  const { configured, canManage } = access;

  const [media, setMedia] = useState<AppleTvMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingName, setPendingName] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [viewer, setViewer] = useState<AppleTvMedia | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppleTvMedia | null>(null);

  useEffect(() => {
    if (!configured) return;
    setLoading(true);
    listMedia()
      .then(setMedia)
      .catch(() => toast.error('Failed to load media'))
      .finally(() => setLoading(false));
  }, [configured]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? media.filter((m) => m.name.toLowerCase().includes(q)) : media),
    [media, q],
  );

  const openUpload = () => {
    setPendingFile(null);
    setPendingName('');
    setPreview(null);
    setProgress(0);
    setUploadOpen(true);
  };

  const stageFile = (file: File | undefined) => {
    if (!file) return;
    if (![...ALLOWED_IMAGE, ...ALLOWED_VIDEO].includes(file.type)) {
      toast.error('Unsupported file. Allowed: JPEG, PNG, GIF, WebP, HEIC, MP4, MOV, M4V, WebM, MPEG.');
      return;
    }
    setPendingFile(file);
    setPendingName(file.name.replace(/\.[^.]+$/, ''));
    setPreview(URL.createObjectURL(file));
  };

  const doUpload = async () => {
    if (!pendingFile || !pendingName.trim()) return;
    setUploading(true);
    setProgress(0);
    try {
      const { uploadUrl, storageKey, contentType } = await getMediaUploadUrl({
        name: pendingName.trim(),
        contentType: pendingFile.type,
        fileName: pendingFile.name,
      });
      await uploadToStorage(uploadUrl, contentType, pendingFile, setProgress);
      const created = await registerMedia({ name: pendingName.trim(), storageKey });
      setMedia((prev) => [created, ...prev]);
      toast.success('Media uploaded');
      setUploadOpen(false);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMedia(deleteTarget.id);
      setMedia((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      toast.success('Media deleted');
    } catch (error) {
      if (isNoLongerAvailable(error)) {
        // Not this account's any more, or already gone: drop it from the list.
        setMedia((prev) => prev.filter((m) => m.id !== deleteTarget.id));
        toast.error('That media is no longer available.');
        return;
      }
      toast.error(
        signageErrorMessage(
          error,
          'Cannot delete — a playlist still uses this file. Clear it from those playlists first.',
        ),
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  const gated = shouldGate(access);

  return (
    <AppleTvPageShell>
      <div className="shrink-0 p-6 pb-0">
        <PageHeader title="Media" subtitle="Images and video your playlists can use." />
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-6">
        {gated ? (
          <AppleTvGate {...access} />
        ) : (
          <Table.Root>
            <Table.Toolbar>
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search media..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {canManage && (
                <Button leftIcon={<Upload className="h-4 w-4" />} onClick={openUpload}>
                  Upload
                </Button>
              )}
            </Table.Toolbar>

            {loading ? (
              <Table.Loading />
            ) : filtered.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
                <Film className="h-10 w-10 text-slate-300" />
                <p className="text-sm text-slate-500">
                  {q ? 'No media matches your search.' : 'No media yet — upload an image or video.'}
                </p>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {filtered.map((m) => (
                    <div
                      key={m.id}
                      className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                    >
                      <div className="flex aspect-square w-full items-center justify-center bg-slate-100">
                        {m.type === 'video' ? (
                          <video src={m.url} className="h-full w-full object-cover" muted />
                        ) : (
                          <img src={m.url} alt={m.name} className="h-full w-full object-cover" />
                        )}
                      </div>
                      {m.type === 'video' && (
                        <span className="absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                          <Film className="h-2.5 w-2.5" /> video
                        </span>
                      )}
                      <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => setViewer(m)}
                          title="Preview"
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-black/50 hover:bg-black/70"
                        >
                          <Eye className="h-3.5 w-3.5 text-white" />
                        </button>
                        {canManage && !m.sharedWithMe && (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(m)}
                            title="Delete"
                            className="flex h-7 w-7 items-center justify-center rounded-full bg-black/50 hover:bg-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-white" />
                          </button>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="truncate text-xs font-medium text-slate-800" title={m.name}>
                          {m.name}
                        </p>
                        <p className="text-[11px] text-slate-500">{fmtSize(m.sizeBytes)}</p>
                        {m.sharedWithMe && (
                          <Badge variant="purple" size="sm" className="mt-1">
                            {SHARED_PLAYLIST_BADGE}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Table.Root>
        )}
      </div>

      {/* Upload dialog */}
      <Modal
        isOpen={uploadOpen}
        onClose={() => !uploading && setUploadOpen(false)}
        title="Upload media"
        closeOnEscape={!uploading}
        closeOnBackdrop={!uploading}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setUploadOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={doUpload} loading={uploading} disabled={!pendingFile || !pendingName.trim()}>
              Upload
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-slate-500">
          Images and videos are supported. Large files upload directly to storage.
        </p>
        <div className="grid gap-4">
          {!pendingFile ? (
            <label
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-8 hover:border-primary hover:bg-primary/5"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                stageFile(e.dataTransfer.files?.[0]);
              }}
            >
              <Upload className="h-8 w-8 text-slate-400" />
              <span className="text-sm text-slate-600">Drop a file here or click to choose</span>
              <span className="text-xs text-slate-400">
                JPEG · PNG · GIF · WebP · HEIC · MP4 · MOV · M4V · WebM · MPEG
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => stageFile(e.target.files?.[0])}
              />
            </label>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  {pendingFile.type.startsWith('video') ? (
                    preview ? (
                      <video src={preview} className="h-full w-full object-cover" muted />
                    ) : (
                      <Film className="h-6 w-6 text-slate-400" />
                    )
                  ) : preview ? (
                    <img src={preview} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageOff className="h-6 w-6 text-slate-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-700">{pendingFile.name}</p>
                  <p className="text-xs text-slate-500">{fmtSize(pendingFile.size)}</p>
                  {!uploading && (
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-1 px-0"
                      onClick={() => {
                        setPendingFile(null);
                        setPreview(null);
                      }}
                    >
                      Choose a different file
                    </Button>
                  )}
                </div>
              </div>
              <FormField label="Name" required>
                <Input
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  placeholder="Display name"
                  disabled={uploading}
                />
              </FormField>
              {uploading && (
                <div className="space-y-1">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-right text-xs text-slate-500">{progress}%</p>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Preview dialog */}
      <Modal isOpen={!!viewer} onClose={() => setViewer(null)} title={viewer?.name} size="xl">
        <div className="flex items-center justify-center py-2">
          {viewer?.type === 'video' ? (
            <video src={viewer.url} controls className="max-h-[60vh] max-w-full rounded-lg" />
          ) : viewer ? (
            <img src={viewer.url} alt={viewer.name} className="max-h-[60vh] max-w-full rounded-lg object-contain" />
          ) : null}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={doDelete}
        title={`Delete “${deleteTarget?.name}”?`}
        message="This removes the file from storage. If any playlist still uses it, the delete is refused and you'll be told which playlists to clear first."
        confirmLabel="Delete"
      />
    </AppleTvPageShell>
  );
}
